/**
 * Components lifted from the Mereka prototype, shared by learner, admin and console.
 *
 * These exist so twelve admin pages do not each invent their own tabs, chips and
 * tables. Every one reads tokens only, so it follows whichever palette `data-app`
 * selected and re-tints with a tenant's branding.
 *
 * Prototype equivalents, kept faithfully:
 *   Tabs      .an-tabs / .an-tab / .an-tab__count  — pill tabs with a count badge
 *   Chip      .chip                                — filter/metadata pill
 *   DataTable .data-table                          — dense listing table
 *   Kpi       .an-entity__kpi                      — label + bold figure
 *   Badge     .badge                               — inline status
 */

import type { ReactNode } from 'react';
import './prototype.css';

export function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={`material-symbols-outlined${className ? ` ${className}` : ''}`} aria-hidden="true">
      {name}
    </span>
  );
}

export interface TabDef {
  id: string;
  label: string;
  /** Shown as a badge. Omit when unknown — a count of 0 and "not loaded yet" differ. */
  count?: number;
  icon?: string;
}

/**
 * Pill tab strip.
 *
 * Radio semantics rather than buttons-in-a-row: a screen reader should announce this
 * as one control with a selected option, which is what it is.
 */
export function Tabs({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: TabDef[];
  active: string;
  onChange: (id: string) => void;
  label: string;
}) {
  return (
    <div className="an-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={`an-tab${tab.id === active ? ' is-active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.icon && <Icon name={tab.icon} />}
          {tab.label}
          {tab.count !== undefined && <span className="an-tab__count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Chip({
  children,
  icon,
  tone,
  onRemove,
}: {
  children: ReactNode;
  icon?: string;
  tone?: 'brand' | 'neutral';
  onRemove?: () => void;
}) {
  return (
    <span className="chip" data-tone={tone ?? 'neutral'}>
      {icon && <Icon name={icon} />}
      {children}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove" className="chip__remove">
          ×
        </button>
      )}
    </span>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}) {
  return (
    <span className="badge" data-tone={tone}>
      {children}
    </span>
  );
}

export function Kpi({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="kpi">
      <strong>{value}</strong>
      <span>{label}</span>
      {hint && <small>{hint}</small>}
    </div>
  );
}

export function KpiRow({ children }: { children: ReactNode }) {
  return <div className="kpi-row">{children}</div>;
}

/**
 * Toolbar above a listing: search on the left, filters and actions on the right.
 *
 * A consistent slot for these matters more than it looks — when every page puts its
 * search box somewhere different, people stop looking for it.
 */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}

export function SearchField({
  value,
  onChange,
  placeholder = 'Search…',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="search">
      <Icon name="search" />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/**
 * Dense listing table.
 *
 * Takes explicit columns so every listing gets the same header treatment, and renders
 * its own empty state — a table with a head and no body reads as broken.
 */
export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Right-aligns and applies tabular figures. */
  numeric?: boolean;
  mono?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty: ReactNode;
}) {
  if (rows.length === 0) return <div className="data-table__empty">{empty}</div>;

  return (
    <div className="ui-table-scroll">
      <table className="ui-table data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.numeric ? 'num' : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[column.numeric && 'num', column.mono && 'mono']
                    .filter(Boolean)
                    .join(' ')}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
